import { Injectable, Renderer2, signal } from '@angular/core';
import { usePreset } from '@primeng/themes';
import { environment } from 'src/environments/environment';
import { PRESET_MAP, ThemeName } from '../theming/presets';

/**
 * localStorage key para persistir a preferência de dark mode do usuário
 * entre reloads. Valor: 'dark' | 'light'. Ausente = light (default).
 */
const DARK_MODE_KEY = 'chronos-dark-mode';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  /**
   * Signal com o estado atual de dark mode. Componentes podem usar via
   * `themeService.darkMode()` em templates (auto-reactive) ou `effect`s.
   * Inicializa lendo do localStorage.
   */
  readonly darkMode = signal<boolean>(this.readDarkPreference());

  constructor() {
    // Aplica o estado inicial no DOM antes do primeiro render. Sem isso,
    // quem recarrega a página em dark mode vê um flash de light no início.
    this.applyDarkClass(this.darkMode());
  }

  /**
   * Alterna dark mode. Adiciona/remove a classe `.dark` no <html>, que
   * é o darkModeSelector configurado em providePrimeNG — dispara a
   * cascade de tokens colorScheme.dark.* no preset. Persiste em
   * localStorage.
   */
  toggleDarkMode(): void {
    const next = !this.darkMode();
    this.darkMode.set(next);
    this.applyDarkClass(next);
    try {
      localStorage.setItem(DARK_MODE_KEY, next ? 'dark' : 'light');
    } catch {
      // localStorage pode estar bloqueado em alguns modos (ex: private).
      // Falhar silenciosamente: o toggle ainda funciona na sessão atual.
    }
  }

  private applyDarkClass(isDark: boolean): void {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }

  private readDarkPreference(): boolean {
    try {
      return localStorage.getItem(DARK_MODE_KEY) === 'dark';
    } catch {
      return false;
    }
  }

  applyTheme(renderer: Renderer2) {
    this.updateFavicon(renderer);
    this.updateLogoAndPreset();
  }

  setFavicon(renderer: Renderer2) {
    this.updateFavicon(renderer);
  }

  private updateFavicon(renderer: Renderer2) {
    const favicon = environment.configuracaoThema.favicon;
    const link: HTMLLinkElement =
      document.querySelector("link[rel*='icon']") ||
      renderer.createElement('link');

    link.type = 'image/x-icon';
    link.rel = 'icon';
    link.href = favicon;

    renderer.appendChild(document.head, link);
  }

  // Applies the active theme preset to PrimeNG at runtime and sets the
  // CSS variable used by the layout header logo. Historically this method
  // was called updateLogo() and also tried to inject a <link rel=stylesheet>
  // carrying the compiled SCSS theme — that branch was dead code (the link
  // was never appended) and was removed in PR1. The preset engine in v18
  // (usePreset) now handles PrimeNG styling entirely via CSS custom
  // properties, so the stylesheet injection is no longer needed.
  private updateLogoAndPreset() {
    const root = document.documentElement;
    root.style.setProperty(
      '--logo-url',
      `url('${environment.configuracaoThema.logo}')`,
    );

    const themeKey = this.resolveThemeName();
    const preset = PRESET_MAP[themeKey];
    if (preset) {
      usePreset(preset);
    }
  }

  private resolveThemeName(): ThemeName {
    const raw = environment.configuracaoThema.theme;
    const normalized = raw.replace(/^theme-/, '') as ThemeName;
    return normalized in PRESET_MAP ? normalized : 'orange';
  }
}
